import { Injectable, signal } from '@angular/core';

/** Error type categories for auth flow errors */
export type ErrorContextType = 'auth-failure' | 'access-denied' | 'account-not-found' | 'token-expired';

/** Error context interface for auth/access errors */
export interface ErrorContext {
  type: ErrorContextType;
  message: string;
  recoveryAction?: 'retry' | 'contact-admin' | 'contact-support';
  timestamp: number;
  correlationId?: string;
}

/**
 * AuthErrorContextService
 *
 * Stores and provides error context across navigation, specifically for auth/access failures that occur during the
 * post-login redirect flow. This allows the unauthorized error page to display contextual error messages and recovery
 * actions.
 *
 * Error context is ephemeral (survives single redirect) and is cleared when navigating away from the unauthorized page,
 * maintaining current auto-logout-on-reload behavior.
 *
 * @example
 *   ```typescript
 *   // In error interceptor
 *   const errorContextService = inject(AuthErrorContextService);
 *   errorContextService.setContext({
 *     type: 'account-not-found',
 *     message: 'Your account could not be found',
 *     recoveryAction: 'contact-admin',
 *     timestamp: Date.now()
 *   });
 *
 *   // In unauthorized component
 *   const context = errorContextService.context();
 *   if (context) {
 *     console.log(context.type); // 'account-not-found'
 *   }
 *   ```;
 */
@Injectable({
  providedIn: 'root',
})
export class AuthErrorContextService {
  /** Signal holding the current error context null = no active error context */
  private readonly _context = signal<ErrorContext | null>(null);

  readonly context = this._context.asReadonly();

  /** Store error context for display in unauthorized page Call this before redirecting to /unauthorized */
  setContext(errorContext: ErrorContext): void {
    this._context.set(errorContext);
  }

  /** Clear error context (call when navigating away from /unauthorized) */
  clearContext(): void {
    this._context.set(null);
  }
}
